import React, {FC, ReactNode} from 'react';
import styled, {css} from 'styled-components';

import {IconBox} from '../../../../../../../../src/components/common/iconBox/iconBox';
import {Handler} from '../../../../../../../shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../../../../shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../../shared/ui/src/components/buttons/buttonConstants';
import {Icon, IconName} from '../../../../../../../shared/ui/src/components/icon/icon';
import {VisualSizesEnum} from '../../../../../../../shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../../../../shared/ui/src/styles/fontStyles';
import {useUserTheme} from '../../../../../../../shared/ui/src/theme/userThemeProvider';
import {useContactsManagerContext} from '../../contactsManagerContext';
import {
  buildContactsManagerInfoBoxClickEvent,
  ContactsManagerInfoBoxActionsEnum,
} from '../../helpers/trackingHelpers';

/*
 * Props.
 */

interface InfoBoxProps {
  iconName?: IconName;
  iconSize?: number;
  borderRadius?: number;
  backgroundColor?: string;
  borderColor?: string;
  isExternalLink?: boolean;
  title?: ReactNode;
  footer?: ReactNode;
  isFullWidth?: boolean;
  isFooterDisabled?: boolean;
  onFooterClick: Handler;
  onRequestClose?: Handler;
}

/*
 * Styles.
 */

const StyledTitleDiv = styled.div`
  color: ${(p) => p.theme.greys.shade80};
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.semibold};
  padding: 0 16px 10px 0;
  line-height: 21px;
`;

const StyledButtonContentDiv = styled.div`
  display: flex;
  flex-flow: row;
  justify-content: center;
  font-weight: ${fontWeights.semibold};
  font-size: ${fontSizes.medium};
  align-items: center;
`;

const StyledButtonIcon = styled(Icon)`
  margin-left: 6px;
`;

interface StyledIconBoxProps {
  $isFullWidth?: boolean;
  $borderColor?: string;
}

const StyledIconBox = styled(IconBox)<StyledIconBoxProps>`
  padding: 16px;

  width: 248px;
  ${(p) =>
    p.$isFullWidth &&
    css`
      width: 100%;
    `}

  ${(p) =>
    p.$borderColor &&
    css`
      border: 1px solid ${p.$borderColor};
    `}
`;

const StyledBodyDiv = styled.div`
  color: ${(p) => p.theme.greys.shade80};
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.normal};
  line-height: 22px;
`;

const StyledContentDiv = styled.div`
  display: flex;
  flex-flow: column;
`;

const StyledButton = styled(Button)`
  width: fit-content;
  padding-top: 6px;
  margin-left: -14px;
`;

/*
 * Component.
 */

export const InfoBox: FC<InfoBoxProps> = ({
  iconName = 'info',
  iconSize = 20,
  backgroundColor,
  borderColor,
  isExternalLink = true,
  borderRadius = 16,
  title,
  footer,
  children,
  isFullWidth,
  isFooterDisabled,
  onFooterClick,
  onRequestClose,
}) => {
  const theme = useUserTheme();
  const {onTrackHandler} = useContactsManagerContext();

  const onRequestCloseHandler = () => {
    if (onRequestClose) {
      onRequestClose();
    }
    const event = buildContactsManagerInfoBoxClickEvent(ContactsManagerInfoBoxActionsEnum.DISMISS);
    onTrackHandler(event);
  };

  const onFooterClickHandler = () => {
    onFooterClick();
    const event = buildContactsManagerInfoBoxClickEvent(ContactsManagerInfoBoxActionsEnum.LEARN_MORE);
    onTrackHandler(event);
  };

  return (
    <StyledIconBox
      iconName={iconName}
      iconSize={iconSize}
      backgroundColor={backgroundColor || theme.greys.shade20}
      borderRadius={borderRadius}
      onRequestClose={onRequestClose ? onRequestCloseHandler : undefined}
      $isFullWidth={isFullWidth}
      $borderColor={borderColor}
    >
      <StyledContentDiv>
        {title && <StyledTitleDiv>{title}</StyledTitleDiv>}
        <StyledBodyDiv>{children}</StyledBodyDiv>
        {renderFooter(footer, isExternalLink, Boolean(isFooterDisabled), onFooterClickHandler)}
      </StyledContentDiv>
    </StyledIconBox>
  );
};

/*
 * Helpers.
 */

function renderFooter(footer: ReactNode, isExternalLink: boolean, isDisabled: boolean, onClick: Handler) {
  if (!footer) {
    return null;
  }
  return (
    <StyledButton
      onClick={onClick}
      size={VisualSizesEnum.MEDIUM}
      variant={ButtonVariantsEnum.LINK}
      isDisabled={isDisabled}
    >
      <StyledButtonContentDiv>
        {footer}
        {isExternalLink && <StyledButtonIcon name="externalLinkSmall" />}
      </StyledButtonContentDiv>
    </StyledButton>
  );
}
